import * as React from 'react';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import styles from './flow.module.css';
import logsStore from '../../stores/logs.store';
import searchStore from '../../stores/search.store';
import memberStore from '../../stores/member.store';
import { SessionComponent, laneClass, flowUrl } from './log_list_view.component';

// Shown on a single-session view whose session is part of a flow, in either direction.
const FlowBanner = observer(() => {
  const history = useHistory();
  const { sessionId } = searchStore;
  const session = sessionId ? logsStore.sessions.find(s => s._id === sessionId) : undefined;
  const isAppSession = !!session && !session.callerSessionId;

  useEffect(() => {
    if (isAppSession) logsStore.loadLinkedApps(session._id);
  }, [isAppSession, session?._id]);

  if (!session) return null;

  const linked = logsStore.linkedApps.get(session._id);
  const isCaller = !!(session.callerApp && session.callerSessionId);
  if (!isCaller && !linked?.length) return null;
  const url = isCaller ? flowUrl(session.callerApp!, session.callerSessionId!) : flowUrl(session.app, session._id);
  // A real href keeps cmd-click / middle-click opening a new tab; a plain click stays in the SPA.
  const onClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    history.push(url);
  };
  return (
    <div className={styles.banner}>
      {isCaller ? <>Triggered by a <b>{memberStore.appName(session.callerApp!)}</b> session</>
        : <>This session made requests to <b>{[...new Set(linked!.map(({ app }) => memberStore.appName(app)))].join(', ')}</b></>}
      <a className={styles.bannerLink} href={url} onClick={onClick}>View full flow →</a>
    </div>
  );
});

// The flow belongs to the app session, so its panel stays fixed on it; a backend app in the legend swaps in that session's details.
const FlowHeader = observer(() => {
  const [panelApp, setPanelApp] = useState<string>();
  if (!logsStore.isFlowData) return null;
  const rootSession = logsStore.sessions.find(session => !session.callerSessionId);
  const panelSession = panelApp ? logsStore.sessions.find(session => session.app === panelApp && session.callerSessionId) : rootSession;
  const legend = (app: string, dotClass: string, selectApp?: string) => (
    <span key={app} className={`${styles.legend} ${panelSession?.app === app ? styles.selected : ''}`} onClick={() => setPanelApp(selectApp)}
      data-tip="show this app's session">
      <span className={`${styles.dot} ${dotClass}`}/>{memberStore.appName(app)}
    </span>
  );
  return (
    <div className={styles.flowHeader}>
      <div className={styles.legendRow}>
        <span className={styles.flowTitle}>Full flow</span>
        {rootSession && legend(rootSession.app, styles.rootDot)}
        {[...logsStore.flowLanes].map(([app, lane]) => legend(app, laneClass(lane), app))}
      </div>
      {panelSession && <SessionComponent session={panelSession}/>}
    </div>
  );
});

// The flow view has its header; any other view offers the way into the flow its session belongs to.
export const Flow = observer(() => searchStore.flowId ? <FlowHeader/> : <FlowBanner/>);
